import { filtersLibrary, AppFilterType } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { GoalPaneLayout } from "@/pages/talent/goals/components/GoalPaneLayout.tsx";
import { useGoalSidePanel } from "@/pages/talent/goals/hooks/use-goal-side-panel.hook.ts";
import { useGoalsCustomFilters } from "@/pages/talent/goals/widgets/GoalsCustomFilters.tsx";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { GoalsTable } from "@/pages/talent/goals/components/GoalsTable.tsx";
import { useGoalsTableHook } from "@/pages/talent/goals/hooks/use-goals-table.hook.ts";
import { useEmployeeGoalsSearch } from "@/queries/employees/use-employee-goals.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useGoalsCounts, useGoalsSearch } from "@/queries/goals.query.ts";
import { useTeamDetails } from "@/queries/use-teams.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiGoalOwnerType,
  ApiGoalSearchFilter,
  ApiGoalStatus,
  GoalConfidenceLevelItems,
} from "@/types/goals.types.ts";
import {
  Button,
  CoreFilterType,
  EmptyStateBase,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Search,
  Stack,
  useDebouncedSearchParam,
  useModals,
} from "@skailer-com/uikit";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export default function SearchGoalsPage() {
  const { t } = useTranslation();
  const { canEditField, canIEdit } = usePermissions();
  const { openModal } = useModals();
  const { pathname } = useLocation();
  const { team_id: teamIdParam, department_id: departmentIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openGoalPanel } = useGoalSidePanel();
  const teamId = Number(teamIdParam);
  const isValidTeamId = teamId > 0;
  const departmentId = Number(departmentIdParam);
  const isValidDepartmentId = departmentId > 0;
  const { data: employeeMe } = useEmployeeMe();
  const { data: teamDetails } = useTeamDetails(teamId, isValidTeamId);
  const {
    value: searchTerm,
    setValue: setSearchTerm,
    clear: clearSearch,
  } = useDebouncedSearchParam({
    paramName: "search",
    delay: 300,
  });

  const isPersonalProfile =
    pathname === "/goals/personal" || pathname.startsWith("/goals/personal");
  const isDepartmentProfile =
    pathname === "/goals/department" || pathname.startsWith("/goals/department");
  const isTeamProfile = pathname.startsWith("/goals/team/");
  const isDirectReportsProfile =
    pathname === "/goals/reports/direct" || pathname.startsWith("/goals/reports/direct");
  const isSubordinatesReportProfile =
    pathname === "/goals/reports/subordinates" ||
    pathname.startsWith("/goals/reports/subordinates");
  const isScopedProfile =
    isPersonalProfile ||
    isDepartmentProfile ||
    isTeamProfile ||
    isDirectReportsProfile ||
    isSubordinatesReportProfile;
  const shouldShowOwnerTypeFilter = !isScopedProfile;
  const shouldShowAssigneeFilter = !isScopedProfile || isDepartmentProfile || isTeamProfile;
  const [usePersonalCustomFilter, setUsePersonalCustomFilter] = useState(false);
  const [initialPersonalParams, setInitialPersonalParams] = useState<string | null>(
    isPersonalProfile ? searchParams.toString() : null
  );

  const library = filtersLibrary();
  const { filterAdditions, renderFilters } = useGoalsCustomFilters();

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiGoalSearchFilter>({
    filters: [
      library.page_number,
      library.text,
      {
        name: "confidence_level",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.multiselect,
        label: t("goals:confidence_level"),
        options: GoalConfidenceLevelItems(),
      },
      ...(shouldShowOwnerTypeFilter
        ? [
            {
              name: "owner_type",
              type: AppFilterType.goalOwner,
              valueType: FilterValueType.stringArray,
              renderType: FilterRenderType.custom,
              label: t("goals:owner_type"),
            },
            {
              name: "owner_id",
              type: CoreFilterType.hidden,
              valueType: FilterValueType.numberArray,
              renderType: FilterRenderType.none,
              hideInFilter: true,
            },
          ]
        : []),
      ...(shouldShowAssigneeFilter
        ? [
            {
              name: "assignee_ids",
              type: AppFilterType.employee,
              valueType: FilterValueType.numberArray,
              renderType: FilterRenderType.custom,
              label: t("goals:assignees"),
            },
          ]
        : []),
    ],
    source: "goals_search",
    renderFilterButton: ({ onClick, count }) => (
      <Button
        variant={count > 0 ? "primary" : "secondary"}
        leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
        rightIcon={count > 0 ? <NotificationCount count={count} size="sm" /> : undefined}
        onClick={onClick}
      >
        {t("common:filter")}
      </Button>
    ),
  });

  const shouldLoadGoalCounts =
    isDepartmentProfile || isDirectReportsProfile || isSubordinatesReportProfile;
  const { data: goalsCounts, isLoading: isGoalsCountsLoading } =
    useGoalsCounts(shouldLoadGoalCounts);
  const departmentItemsWithGoals = useMemo(
    () => (goalsCounts?.departments ?? []).filter((departmentItem) => departmentItem.count > 0),
    [goalsCounts]
  );
  const selectedDepartmentItem = useMemo(
    () =>
      isValidDepartmentId
        ? departmentItemsWithGoals.find(
            (departmentItem) => departmentItem.department.id === departmentId
          )
        : undefined,
    [departmentId, departmentItemsWithGoals, isValidDepartmentId]
  );

  const departmentOwnerIds = useMemo(
    () => (selectedDepartmentItem ? [selectedDepartmentItem.department.id] : []),
    [selectedDepartmentItem]
  );
  const directReportIds = goalsCounts?.direct_reports?.employee_ids ?? [];
  const subordinatesReportIds = goalsCounts?.subordinates?.employee_ids ?? [];

  const hasGoalCounts = goalsCounts !== undefined;
  const hasDirectReports = directReportIds.length > 0;
  const hasSubordinateReports = subordinatesReportIds.length > 0;
  const hasDepartments = departmentOwnerIds.length > 0;

  const goalsSearchEnabled = useMemo(() => {
    if (isPersonalProfile) return !!employeeMe?.id;
    if (isDepartmentProfile) return hasGoalCounts && hasDepartments;
    if (isTeamProfile) return isValidTeamId;
    if (isDirectReportsProfile) return hasGoalCounts && hasDirectReports;
    if (isSubordinatesReportProfile) return hasGoalCounts && hasSubordinateReports;
    return true;
  }, [
    employeeMe?.id,
    hasDepartments,
    hasGoalCounts,
    hasDirectReports,
    hasSubordinateReports,
    isDepartmentProfile,
    isDirectReportsProfile,
    isSubordinatesReportProfile,
    isPersonalProfile,
    isTeamProfile,
    isValidTeamId,
  ]);

  const isProfileScopeLoading = useMemo(() => {
    if (isDepartmentProfile) return isGoalsCountsLoading;
    if (isDirectReportsProfile) return isGoalsCountsLoading;
    if (isSubordinatesReportProfile) return isGoalsCountsLoading;
    return false;
  }, [
    isDepartmentProfile,
    isGoalsCountsLoading,
    isDirectReportsProfile,
    isSubordinatesReportProfile,
  ]);

  const profileFilter = useMemo<Partial<ApiGoalSearchFilter>>(() => {
    if (isPersonalProfile) {
      if (!employeeMe?.id) return {};
      return {
        owner_type: [ApiGoalOwnerType.employee],
        owner_id: [employeeMe?.id],
      };
    }

    if (isDepartmentProfile) {
      if (departmentOwnerIds.length === 0) return {};
      return {
        owner_type: [ApiGoalOwnerType.department],
        owner_id: departmentOwnerIds,
      };
    }

    if (isTeamProfile) {
      if (!isValidTeamId) return {};
      return {
        owner_type: [ApiGoalOwnerType.team],
        owner_id: [teamId],
      };
    }

    if (isDirectReportsProfile) {
      if (!hasDirectReports) return {};
      return {
        owner_type: [ApiGoalOwnerType.employee],
        owner_id: directReportIds,
      };
    }

    if (isSubordinatesReportProfile) {
      if (!hasSubordinateReports) return {};
      return {
        owner_type: [ApiGoalOwnerType.employee],
        owner_id: subordinatesReportIds,
      };
    }

    return {};
  }, [
    departmentOwnerIds,
    directReportIds,
    employeeMe?.id,
    hasDirectReports,
    hasSubordinateReports,
    isDepartmentProfile,
    isDirectReportsProfile,
    isSubordinatesReportProfile,
    isPersonalProfile,
    isTeamProfile,
    isValidTeamId,
    subordinatesReportIds,
    teamId,
  ]);

  const filterWithCustom = useMemo<ApiGoalSearchFilter>(() => {
    return {
      ...filter,
      ...filterAdditions,
      ...profileFilter,
      only_top_level: false,
    };
  }, [filter, filterAdditions, profileFilter]);

  const personalFilterWithCustom = useMemo<ApiGoalSearchFilter>(() => {
    if (!usePersonalCustomFilter) {
      return {
        status: [ApiGoalStatus.active],
      };
    }

    return {
      ...filter,
      ...filterAdditions,
    };
  }, [filter, filterAdditions, usePersonalCustomFilter]);

  const searchParamsString = searchParams.toString();
  if (!isPersonalProfile) {
    if (initialPersonalParams !== null) setInitialPersonalParams(null);
    if (usePersonalCustomFilter) setUsePersonalCustomFilter(false);
  } else if (initialPersonalParams === null) {
    setInitialPersonalParams(searchParamsString);
  } else if (!usePersonalCustomFilter && searchParamsString !== initialPersonalParams) {
    setUsePersonalCustomFilter(true);
  }

  const { data: personalGoalsData, isLoading: isPersonalGoalsLoading } = useEmployeeGoalsSearch(
    employeeMe?.id || 0,
    personalFilterWithCustom,
    isPersonalProfile && goalsSearchEnabled
  );

  const { data: goalsData, isLoading: isGoalsSearchLoading } = useGoalsSearch(
    filterWithCustom,
    goalsSearchEnabled && !isPersonalProfile
  );

  const visibleGoalsData = goalsSearchEnabled
    ? isPersonalProfile
      ? personalGoalsData
      : goalsData
    : undefined;

  const isGoalsLoading =
    (isPersonalProfile ? isPersonalGoalsLoading : isGoalsSearchLoading) || isProfileScopeLoading;

  const {
    flatGoals,
    expandedPaths,
    loadingPaths,
    toggleExpand,
    handleEditGoal,
    handleDetailsGoal,
    handleDeleteGoal,
  } = useGoalsTableHook(visibleGoalsData?.items ?? []);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((searchParams) => {
        if (newData) searchParams.set("search", newData);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
  });

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.goals.listShown);
  });

  const handleCreateGoal = () => {
    openModal(GoalEditModal, {
      owner_employee: isPersonalProfile ? employeeMe : undefined,
      disableOwnerTypeChoice: isPersonalProfile,
    });
  };

  const canCreateManagedGoal =
    canIEdit(ApiPermission.goals_company_manage) ||
    canIEdit(ApiPermission.goals_department_manage_all) ||
    canIEdit(ApiPermission.goals_department_manage_own) ||
    canIEdit(ApiPermission.goals_team_manage_all) ||
    canIEdit(ApiPermission.goals_team_manage_own);
  const canCreate = isPersonalProfile
    ? canEditField(employeeMe, EmployeeFieldPermission.employee_goals)
    : canCreateManagedGoal;

  const pageTitle = useMemo(() => {
    if (isPersonalProfile) return t("goals:my_goals");
    if (isDepartmentProfile) {
      if (selectedDepartmentItem) return selectedDepartmentItem.department.title;
      if (departmentItemsWithGoals.length > 1) return t("core:departments");
      return t("core:department");
    }
    if (isTeamProfile) return teamDetails?.title || t("core:team");
    if (isDirectReportsProfile) return t("core:direct_reports");
    if (isSubordinatesReportProfile) return t("core:all_subordinates");
    return t("goals:search_goals");
  }, [
    departmentItemsWithGoals,
    isDepartmentProfile,
    isDirectReportsProfile,
    isSubordinatesReportProfile,
    isPersonalProfile,
    selectedDepartmentItem,
    isTeamProfile,
    teamDetails?.title,
    t,
  ]);

  useTitle(pageTitle);

  return (
    <GoalPaneLayout>
      <Page className="h-full">
        <Page.Header title={pageTitle}>
          <Stack direction={"horizontal"} gap={"sm"}>
            {renderFilters()}
            {filterButton()}
            {canCreate && (
              <Button variant="primary" onClick={handleCreateGoal}>
                {t("goals:create_goal")}
              </Button>
            )}
          </Stack>
        </Page.Header>
        <Page.Content stretch>
          <Page.StickyLayout
            toolbar={
              <Search
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
                clearable={true}
                onClear={clearSearch}
              />
            }
            footer={
              visibleGoalsData ? (
                <PaginationBlock
                  metadata={visibleGoalsData.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              ) : undefined
            }
          >
            <GoalsTable
              stickyHeader
              flatGoals={flatGoals}
              isLoading={isGoalsLoading}
              expandedPaths={expandedPaths}
              loadingPaths={loadingPaths}
              toggleExpand={toggleExpand}
              handleEditGoal={handleEditGoal}
              handleDetailsGoal={handleDetailsGoal}
              handleDeleteGoal={handleDeleteGoal}
              onItemClick={openGoalPanel}
              emptyStateComponent={
                <EmptyStateBase
                  emoji={":dart:"}
                  title={t("goals:goals_empty_state_title")}
                  description={t("goals:goals_empty_state_description")}
                  primaryButton={
                    canCreate && (
                      <Button onClick={handleCreateGoal}>{t("goals:create_goal")}</Button>
                    )
                  }
                />
              }
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </GoalPaneLayout>
  );
}
